import { useMemo } from 'react';


export interface OnboardingSteps {
  IsSkipForNow?: boolean;
  IsCloseSteps?: boolean;
  isFirstTimeUser?: boolean;
  isNewUserSkipped?: boolean;
  [key: string]: any;
}
export interface OnboardingStatus {
  isSkipForNow: boolean | null;
  isOnboardingIncomplete: boolean;
  isFirstTimeUser?: boolean;
  isNewUserSkipped?: boolean;
  isOnboardingComplete: boolean;
  steps: OnboardingSteps;
  hasValidSteps: boolean;
}

/**
 * Utility: Safely parse onboarding steps JSON
 * @param json - JSON string to parse
 * @returns Parsed object or empty object if parsing fails
 */
const safeParseSteps = (json: string | undefined): OnboardingSteps => {
  if (!json) return {};

  try {

    const parsed = JSON.parse(json);
    return typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('Failed to parse onboarding steps:', error);
    return {};
  }
};

/**
 * Utility: Compute onboarding flags from steps
 * @param steps - Parsed onboarding steps
 * @returns Computed onboarding status flags
 */
const computeOnboardingFlags = (steps: OnboardingSteps) => {
  const hasValidSteps = Object.keys(steps).length > 0;
  const isSkipForNow = hasValidSteps ? Boolean(steps?.IsSkipForNow) : true;
  const isOnboardingComplete = hasValidSteps ? Boolean(steps?.IsCloseSteps) : true;
  const isOnboardingIncomplete = !isOnboardingComplete;
  const isFirstTimeUser = hasValidSteps ? Boolean(steps?.IsFirstTimeUser) : false;
  const isNewUserSkipped = hasValidSteps ? Boolean(steps?.IsNewUserSkipped) : false;

  return {
    isSkipForNow,
    isOnboardingIncomplete,
    isOnboardingComplete,
    isFirstTimeUser,
    isNewUserSkipped,
    hasValidSteps
  };
};

/**
 * Custom hook to manage and compute onboarding status 
 * @param userOnboardingData - User data object containing onBoardingSteps JSON string
 * @returns OnboardingStatus with computed flags and parsed steps
 */
export const useOnboardingStatus = (userOnboardingData: any): OnboardingStatus => {
  return useMemo(() => {
    const onBoardingSteps = userOnboardingData?.onBoardingSteps;
    

    const steps = safeParseSteps(onBoardingSteps);
    const flags = computeOnboardingFlags(steps);

    return {
      ...flags,
      steps
    };
  }, [userOnboardingData?.onBoardingSteps]);
};

